/**
 * ReminderList - Display and manage reminders for Device PWA
 * 
 * Features:
 * - Fetch and display reminders for current senior
 * - Sort by scheduled time
 * - Create new reminder button
 * - Edit/Delete actions per reminder
 * - Large, senior-friendly UI
 */
import React, { useState, useEffect } from 'react';
import { Bell, Clock, Plus, Edit, Trash2, Loader, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

interface Reminder {
  id: string;
  user_id: string;
  title: string;
  scheduled_time: string;
  repeat: 'none' | 'daily' | 'weekly' | 'monthly';
  reminder_type?: string;
  status?: string;
  created_at?: string;
}

interface ReminderListProps {
  senior: {
    id: string;
    name: string;
  };
  onNavigateNew?: () => void;
  onNavigateEdit?: (id: string) => void;
}

export default function ReminderList({ senior, onNavigateNew, onNavigateEdit }: ReminderListProps) {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (senior?.id) {
      fetchReminders();
    }
  }, [senior]);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API}/api/reminders?senior_id=${senior.id}`);
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Erinnerungen');
      }
      
      const data = await response.json();
      
      // Sort by scheduled_time ascending
      const sorted = (data || []).sort((a: Reminder, b: Reminder) => {
        return new Date(a.scheduled_time).getTime() - new Date(b.scheduled_time).getTime();
      });
      
      setReminders(sorted);
      console.log('[ReminderList] Loaded', sorted.length, 'reminders');
    } catch (err) {
      console.error('[ReminderList] Error:', err);
      setError('Erinnerungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reminderId: string) => {
    if (!window.confirm('Möchten Sie diese Erinnerung wirklich löschen?')) {
      return;
    }

    try {
      setDeleting(reminderId);
      const response = await fetch(`${API}/api/reminders/${reminderId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen');
      }

      toast.success('Erinnerung gelöscht');
      
      // Remove from list
      setReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch (err) {
      console.error('[ReminderList] Delete error:', err);
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleting(null);
    }
  };

  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return isoString;
    }
  };

  const formatDate = (isoString: string) => {
    try {
      const date = new Date(isoString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (date.toDateString() === today.toDateString()) {
        return 'Heute';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return 'Morgen';
      } else {
        return date.toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit' 
        });
      }
    } catch {
      return '';
    }
  };

  const getRepeatLabel = (repeat: string) => {
    const labels: Record<string, string> = {
      none: 'Einmalig',
      daily: 'Täglich',
      weekly: 'Wöchentlich',
      monthly: 'Monatlich'
    };
    return labels[repeat] || repeat;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen">
        <div className="text-center">
          <Loader className="w-20 h-20 mx-auto mb-6 animate-spin text-green-600" />
          <p className="text-3xl text-gray-600 font-semibold">Erinnerungen werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen">
        <div className="text-center px-6">
          <AlertCircle className="w-24 h-24 mx-auto mb-6 text-red-500" />
          <p className="text-3xl text-gray-700 mb-8 font-semibold">{error}</p>
          <button
            onClick={fetchReminders}
            className="px-10 py-5 bg-green-500 hover:bg-green-600 text-white rounded-2xl text-2xl font-bold shadow-lg"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-blue-50 p-6 pb-32">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-5xl font-bold text-gray-900 mb-4 flex items-center gap-4">
          <Bell className="w-12 h-12 text-green-600" />
          Erinnerungen
        </h1>
        <p className="text-2xl text-gray-600">für {senior.name}</p>
      </div>

      {/* Add Button */}
      <button
        onClick={onNavigateNew}
        className="w-full mb-8 px-8 py-6 bg-green-500 hover:bg-green-600 text-white rounded-2xl text-2xl font-bold shadow-lg flex items-center justify-center gap-4 transition-all"
      >
        <Plus className="w-8 h-8" />
        Neue Erinnerung hinzufügen
      </button>

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <div className="text-center py-16">
          <Bell className="w-32 h-32 mx-auto mb-6 text-gray-300" />
          <p className="text-3xl text-gray-500 font-semibold">Keine Erinnerungen</p>
          <p className="text-xl text-gray-400 mt-4">Fügen Sie Ihre erste Erinnerung hinzu</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reminders.map((reminder) => (
            <div
              key={reminder.id}
              className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-green-300 transition-all"
            >
              {/* Title & Time */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">
                    {reminder.title}
                  </h3>
                  <div className="flex items-center gap-4 text-2xl text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock className="w-6 h-6 text-green-600" />
                      <span className="font-semibold">{formatTime(reminder.scheduled_time)}</span>
                    </div>
                    <span className="text-gray-400">•</span>
                    <span>{formatDate(reminder.scheduled_time)}</span>
                  </div>
                </div>
              </div>

              {/* Repeat Info */}
              {reminder.repeat !== 'none' && (
                <div className="mb-4 inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-xl text-lg font-semibold">
                  {getRepeatLabel(reminder.repeat)}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => onNavigateEdit && onNavigateEdit(reminder.id)}
                  className="flex-1 px-6 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-all"
                >
                  <Edit className="w-6 h-6" />
                  Bearbeiten
                </button>
                <button
                  onClick={() => handleDelete(reminder.id)}
                  disabled={deleting === reminder.id}
                  className="flex-1 px-6 py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting === reminder.id ? (
                    <Loader className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-6 h-6" />
                      Löschen
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
